import React, {
  createContext, useContext, useMemo, useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import equal from 'fast-deep-equal';
import debounce from 'lodash/debounce';

type TFiltersType = object;

export interface FiltersStateContext<T extends TFiltersType> {
  filters: T;
  setFilters: (values: T) => void;
  updateFilters: (values: Partial<T>) => void;
  updateFiltersDebounced: (values: Partial<T>) => void;
}

type GenericProviderProps<T extends TFiltersType> = PropsWithChildren<{
  initialData?: T;
}>;

interface CreateGenericContextProps {
  displayName: string;
}

type CreateGenericContextReturn<T extends TFiltersType> = [
  () => FiltersStateContext<T>,
  React.FC<GenericProviderProps<T>>,
];

function createGenericFiltersContext<T extends TFiltersType>({
  displayName,
}: CreateGenericContextProps): CreateGenericContextReturn<T> {
  // Create the context instance. The default value should match the type definition.
  // We use undefined here and rely on the provider to always supply a value.
  const FiltersContext = createContext<FiltersStateContext<T> | undefined>(undefined);

  // Custom hook to consume the context
  const useGenericFiltersContext = () => {
    const context = useContext(FiltersContext);
    if (context === undefined) {
      throw new Error(`${displayName} context must be used within a ${displayName}Provider`);
    }
    return context;
  };

  // Provider component
  function FiltersProvider({
    children = undefined,
    initialData = undefined,
  }: GenericProviderProps<T>): React.JSX.Element {
    const [filters, setFilters] = useState<T>(initialData ?? {} as T);

    const updateFilters = useMemo(
      () => (values: Partial<T>): void => {
        setFilters((prevValues) => {
          const nextValues = { ...prevValues, ...values };
          return equal(prevValues, nextValues) ? prevValues : (nextValues as T);
        });
      },
      [],
    );

    const updateFiltersDebounced = useMemo(() => debounce((updateFilters), 200), [updateFilters]);

    const contextValue = useMemo<FiltersStateContext<T>>(() => {
      const $setFilters = (values: T): void => {
        setFilters((prevValues) => (equal(prevValues, values) ? prevValues : values));
      };

      return {
        filters,
        setFilters: $setFilters,
        updateFilters,
        updateFiltersDebounced,
      };
    }, [filters, updateFilters, updateFiltersDebounced]);

    return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
  }

  FiltersProvider.displayName = `${displayName}Provider`;

  return [useGenericFiltersContext, FiltersProvider] as const;
}

export { createGenericFiltersContext };
